import type {
  PlannedDimension,
  PlannedFilter,
  PlannedMeasure,
  QueryPlan,
} from '@aibi/analysis-engine';
import {
  CONTRACTS_VERSION,
  type QueryResult,
  type QueryResultColumn,
  type QueryResultRow,
  type ScalarValue,
  type TimeGrain,
} from '@aibi/contracts';
import { AppError, err, fitId, ok, stableHash } from '@aibi/shared';
import type { QueryResultSource } from './query-result-source';

export const PREVIEW_WARNING_CODE = 'PREVIEW_DATA';
const DEFAULT_MAX_ROWS = 500;
const FALLBACK_MEMBERS = 5;

export interface PreviewOptions {
  /** Miembros de ejemplo por `dimensionId`; si faltan se generan etiquetas genericas. */
  readonly members?: (dimensionId: string) => readonly ScalarValue[] | undefined;
  /** Reloj inyectable: con un reloj fijo el resultado es identico byte a byte. */
  readonly now?: () => Date;
  /** Latencia simulada para ejercitar los estados de carga (0 en tests). */
  readonly latencyMs?: number;
  readonly maxRows?: number;
}

/**
 * Fuente de datos de DEMOSTRACION. Genera un `QueryResult` sintetico y determinista a
 * partir del `QueryPlan`: los miembros salen del catalogo demo y los valores de un hash
 * estable de (medida, fila). Respeta filtros y selecciones de dimension sobre los miembros
 * y marca el resultado con la advertencia `PREVIEW_DATA`. No ejecuta SQL ni lee datos.
 */
export function createPreviewQueryResultSource(options: PreviewOptions = {}): QueryResultSource {
  const latency = options.latencyMs ?? 0;
  return {
    kind: 'preview',
    load: (plan, loadOptions = {}) =>
      new Promise((resolve) => {
        const { signal } = loadOptions;
        const cancelled = () => {
          resolve(err(new AppError('CANCELLED', 'La carga de datos se cancelo.')));
        };
        if (signal?.aborted === true) {
          cancelled();
          return;
        }
        const run = () => {
          if (signal?.aborted === true) {
            cancelled();
            return;
          }
          try {
            resolve(ok(generatePreviewResult(plan, options)));
          } catch (cause) {
            resolve(
              err(new AppError('INTERNAL_ERROR', 'No se pudo generar la vista previa.', { cause })),
            );
          }
        };
        if (latency <= 0) {
          run();
          return;
        }
        const timer = setTimeout(run, latency);
        signal?.addEventListener('abort', () => {
          clearTimeout(timer);
          cancelled();
        });
      }),
  };
}

/** Generacion pura del resultado de preview (expuesta para pruebas). */
export function generatePreviewResult(plan: QueryPlan, options: PreviewOptions = {}): QueryResult {
  const maxRows = options.maxRows ?? DEFAULT_MAX_ROWS;
  const rowFilters = plan.filters.filter(
    (filter): filter is PlannedFilter & { target: { kind: 'dimension' } } =>
      filter.stage === 'row' && filter.target.kind === 'dimension',
  );

  // Miembros por dimension tras aplicar los filtros que la afectan.
  const memberLists = plan.dimensions.map((dimension) => {
    const own = rowFilters.filter((filter) => filter.target.dimensionId === dimension.dimensionId);
    return membersOf(dimension, options).filter((member) =>
      own.every((filter) => matches(filter, member)),
    );
  });

  // Filtros sobre dimensiones ausentes: se simulan reduciendo los valores proporcionalmente.
  const planned = new Set(plan.dimensions.map((dimension) => dimension.dimensionId));
  let factor = 1;
  for (const filter of rowFilters) {
    if (planned.has(filter.target.dimensionId)) continue;
    const universe = options.members?.(filter.target.dimensionId);
    if (!universe || universe.length === 0) {
      factor *= 0.5;
      continue;
    }
    factor *= universe.filter((member) => matches(filter, member)).length / universe.length;
  }

  const tuples = cartesian(memberLists, maxRows + 1);
  const truncated = tuples.length > maxRows;
  const comparison = plan.comparison;

  let rows: QueryResultRow[] = tuples.slice(0, maxRows).map((tuple) => {
    const trend = 1 + 0.035 * temporalIndex(plan.dimensions, memberLists, tuple);
    const tupleKey = tuple.map((value) => String(value)).join('|');
    const measureValues = plan.measures.map((measure) =>
      round(measure, measureValue(measure, tupleKey) * factor * trend),
    );
    const comparisonValues =
      comparison?.outputs.map((output) => {
        const index = plan.measures.findIndex((measure) => measure.key === output.measureKey);
        const current = measureValues[index] ?? 0;
        const previous = current * (0.85 + 0.1 * unit(`${output.key}|${tupleKey}`));
        if (output.output === 'value') return roundTo(previous, 2);
        if (output.output === 'difference') return roundTo(current - previous, 2);
        return previous === 0 ? null : roundTo((current - previous) / previous, 4);
      }) ?? [];
    return [...tuple, ...measureValues, ...comparisonValues];
  });

  const columns: QueryResultColumn[] = [
    ...plan.dimensions.map((dimension) => dimensionColumn(dimension)),
    ...plan.measures.map((measure): QueryResultColumn => ({
      key: measure.key,
      label: measure.label,
      dataType: measure.dataType,
      role: 'measure',
      fieldId: measure.measureId,
      ...(measure.format ? { format: measure.format } : {}),
    })),
    ...(comparison?.outputs.map((output): QueryResultColumn => {
      const measure = plan.measures.find((candidate) => candidate.key === output.measureKey);
      return {
        key: output.key,
        label: `${measure?.label ?? output.measureKey} (${comparisonLabel(output.output)})`,
        dataType: 'decimal',
        role: 'comparison',
        fieldId: measure?.measureId ?? output.measureKey,
        ...(output.output === 'percent-change' ? { format: { type: 'percent', decimals: 1 } } : {}),
      };
    }) ?? []),
  ];

  // Filtros sobre medidas agregadas.
  for (const filter of plan.filters) {
    if (filter.stage !== 'aggregate' || filter.target.kind !== 'measure') continue;
    const { measureKey } = filter.target;
    const position = columns.findIndex((column) => column.key === measureKey);
    if (position < 0) continue;
    rows = rows.filter((row) => matches(filter, row[position] ?? null));
  }

  rows = sortRows(rows, columns, plan);
  const { limit, offset } = plan.pagination;
  const totalRows = rows.length;
  if (offset > 0) rows = rows.slice(offset);
  if (limit !== null) rows = rows.slice(0, limit);

  const grandTotal = plan.grouping.grandTotal
    ? columns.map((column, i) => {
        if (column.role === 'dimension') return null;
        const values = rows.map((row) => Number(row[i] ?? 0));
        const sum = values.reduce((total, value) => total + value, 0);
        const measure = plan.measures.find((candidate) => candidate.key === column.key);
        const averaged = measure !== undefined && isAverageLike(measure);
        return roundTo(averaged && values.length > 0 ? sum / values.length : sum, 2);
      })
    : undefined;

  return {
    schemaVersion: CONTRACTS_VERSION,
    requestId: fitId(`preview-${plan.fingerprint}`),
    semanticModelId: plan.semanticModelId,
    analysisStateId: plan.analysisStateId,
    columns,
    rows,
    ...(grandTotal ? { totals: { grandTotal, subtotals: [] } } : {}),
    pagination: {
      offset,
      pageSize: Math.max(1, rows.length),
      returnedRows: rows.length,
      totalRows,
      hasMore: false,
    },
    execution: {
      executedAt: (options.now?.() ?? new Date()).toISOString(),
      durationMs: 0,
      engine: 'preview',
      cache: { hit: false },
      truncated,
    },
    warnings: [
      {
        code: PREVIEW_WARNING_CODE,
        message: 'Datos de demostracion generados localmente; no provienen de una fuente real.',
      },
    ],
  };
}

const comparisonLabel = (output: 'value' | 'difference' | 'percent-change'): string =>
  output === 'value' ? 'anterior' : output === 'difference' ? 'diferencia' : 'variacion';

function dimensionColumn(dimension: PlannedDimension): QueryResultColumn {
  const grain = effectiveGrain(dimension);
  return {
    key: dimension.key,
    label: dimension.label,
    dataType: grain === null ? dimension.dataType : grain === 'hour' ? 'datetime' : 'date',
    role: 'dimension',
    fieldId: dimension.dimensionId,
    ...(grain === null ? {} : { timeGrain: grain }),
    ...(dimension.format ? { format: dimension.format } : {}),
  };
}

function effectiveGrain(dimension: PlannedDimension): TimeGrain | null {
  if (dimension.timeGrain !== null) return dimension.timeGrain;
  return dimension.role === 'temporal' ||
    dimension.dataType === 'date' ||
    dimension.dataType === 'datetime'
    ? 'month'
    : null;
}

function membersOf(dimension: PlannedDimension, options: PreviewOptions): readonly ScalarValue[] {
  const grain = effectiveGrain(dimension);
  if (grain !== null) return periodMembers(grain);
  const provided = options.members?.(dimension.dimensionId);
  if (provided && provided.length > 0) return provided;
  return Array.from({ length: FALLBACK_MEMBERS }, (_, i) => `${dimension.label} ${String(i + 1)}`);
}

const pad = (value: number): string => String(value).padStart(2, '0');

/** Periodos fijos (2025-2026) por grano, en ISO 8601. */
export function periodMembers(grain: TimeGrain): string[] {
  switch (grain) {
    case 'year':
      return ['2022', '2023', '2024', '2025', '2026'].map((year) => `${year}-01-01`);
    case 'quarter':
      return [2025, 2026].flatMap((year) =>
        [1, 4, 7, 10].map((month) => `${String(year)}-${pad(month)}-01`),
      );
    case 'month':
      return Array.from({ length: 12 }, (_, i) => `2025-${pad(i + 1)}-01`);
    case 'week':
      return Array.from({ length: 12 }, (_, i) =>
        new Date(Date.UTC(2025, 0, 6 + i * 7)).toISOString().slice(0, 10),
      );
    case 'day':
      return Array.from({ length: 31 }, (_, i) => `2025-01-${pad(i + 1)}`);
    case 'hour':
      return Array.from({ length: 24 }, (_, i) => `2025-01-01T${pad(i)}:00:00.000Z`);
  }
}

function matches(filter: PlannedFilter, value: ScalarValue): boolean {
  if (value === null) {
    if (filter.operator === 'is-null') return true;
    return filter.includeNull;
  }
  const values = filter.values;
  const first = values[0] ?? null;
  const text = String(value).toLowerCase();
  const firstText = String(first).toLowerCase();
  switch (filter.operator) {
    case 'eq':
      return value === first;
    case 'neq':
      return value !== first;
    case 'in':
      return values.includes(value);
    case 'not-in':
      return !values.includes(value);
    case 'gt':
      return compare(value, first) > 0;
    case 'gte':
      return compare(value, first) >= 0;
    case 'lt':
      return compare(value, first) < 0;
    case 'lte':
      return compare(value, first) <= 0;
    case 'between':
      return compare(value, first) >= 0 && compare(value, values[1] ?? null) <= 0;
    case 'contains':
      return text.includes(firstText);
    case 'starts-with':
      return text.startsWith(firstText);
    case 'ends-with':
      return text.endsWith(firstText);
    case 'is-null':
      return false;
    case 'is-not-null':
      return true;
  }
}

function compare(a: ScalarValue, b: ScalarValue): number {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function cartesian(lists: readonly (readonly ScalarValue[])[], cap: number): ScalarValue[][] {
  let tuples: ScalarValue[][] = [[]];
  for (const list of lists) {
    const next: ScalarValue[][] = [];
    for (const tuple of tuples) {
      for (const member of list) {
        next.push([...tuple, member]);
        if (next.length >= cap) break;
      }
      if (next.length >= cap) break;
    }
    tuples = next;
  }
  return tuples;
}

function temporalIndex(
  dimensions: readonly PlannedDimension[],
  lists: readonly (readonly ScalarValue[])[],
  tuple: readonly ScalarValue[],
): number {
  const position = dimensions.findIndex((dimension) => effectiveGrain(dimension) !== null);
  if (position < 0) return 0;
  return Math.max(0, lists[position]?.indexOf(tuple[position] ?? null) ?? 0);
}

/** Numero pseudoaleatorio estable en [0, 1) derivado del texto. */
function unit(text: string): number {
  return parseInt(stableHash(text, 6), 36) / 36 ** 6;
}

const isAverageLike = (measure: PlannedMeasure): boolean =>
  measure.aggregation === 'avg' ||
  measure.aggregation === 'median' ||
  measure.kind === 'expression';

function measureValue(measure: PlannedMeasure, tupleKey: string): number {
  const r = unit(`${measure.measureId}|${tupleKey}`);
  if (measure.kind === 'expression') return 50 + r * 450;
  switch (measure.aggregation) {
    case 'count':
    case 'count-distinct':
      return 40 + r * 460;
    case 'avg':
    case 'median':
      return 4 + r * 60;
    case 'min':
    case 'max':
      return 1 + r * 99;
    case 'sum':
    case null:
      return measure.format?.type === 'currency' || measure.semanticType === 'currency'
        ? 8_000 + r * 92_000
        : 200 + r * 4_800;
  }
}

const roundTo = (value: number, decimals: number): number => {
  const scale = 10 ** decimals;
  return Math.round(value * scale) / scale;
};

const round = (measure: PlannedMeasure, value: number): number =>
  measure.dataType === 'integer' ? Math.round(value) : roundTo(value, 2);

function sortRows(
  rows: QueryResultRow[],
  columns: readonly QueryResultColumn[],
  plan: QueryPlan,
): QueryResultRow[] {
  const keys = plan.sorting
    .map((sort) => ({ position: columns.findIndex((column) => column.key === sort.key), sort }))
    .filter((entry) => entry.position >= 0);
  if (keys.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const { position, sort } of keys) {
      const order = compare(a[position] ?? null, b[position] ?? null);
      if (order !== 0) return sort.direction === 'asc' ? order : -order;
    }
    return 0;
  });
}
